import type { ReactNode } from "react";
import { Box, Column, Row, Skeleton, SkeletonBox, Text } from "@hightouch/htui";
import { LinkButton } from "src/router";

export const IDEAS_PATH = "/ad-studio/ideas";
export const LATEST_IDEAS_LIMIT = 6;

const SKELETON_KEYS = ["a", "b", "c", "d", "e", "f"];

// Caps tiles at this size on wide screens; the strip, header included, stops growing and centers.
const TILE_MAX_SIZE_PX = 216;
// A column drops whenever tiles would shrink below this; under MIN_COLUMNS the remaining tiles shrink instead.
const TILE_MIN_SIZE_PX = 160;
const MIN_COLUMNS = 2;
// Space token 2, in px because container queries can't read CSS variables.
const TILE_GAP_PX = 8;

function stripWidthPx(columns: number, tileSizePx: number) {
  return columns * tileSizePx + (columns - 1) * TILE_GAP_PX;
}

const SECTION_MAX_WIDTH = `${stripWidthPx(LATEST_IDEAS_LIMIT, TILE_MAX_SIZE_PX)}px`;

// Ordered widest first so each narrower query overrides the one before it.
const COLUMN_STEPS = Object.fromEntries(
  Array.from(
    { length: LATEST_IDEAS_LIMIT - MIN_COLUMNS },
    (_, i) => LATEST_IDEAS_LIMIT - 1 - i,
  ).map((columns) => [
    `@container (width < ${stripWidthPx(columns + 1, TILE_MIN_SIZE_PX)}px)`,
    {
      gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
      [`& > :nth-child(n + ${columns + 1})`]: { display: "none" },
    },
  ]),
);

// Kept free of data hooks so the landing skeleton can import it without
// pulling the page chunk into the main bundle.
export function LatestIdeasSection({ children }: { children: ReactNode }) {
  return (
    <Column gap={2} width="100%" maxWidth={SECTION_MAX_WIDTH} mx="auto">
      <Row align="flex-end" justify="space-between" gap={2}>
        <Row align="baseline" gap={2} minWidth={0}>
          <Text size="lg" fontWeight="medium">
            Ideas
          </Text>
          <Text size="sm" color="text.tertiary">
            Updates every week
          </Text>
        </Row>
        <LinkButton href={IDEAS_PATH} size="sm" variant="tertiary">
          Show all
        </LinkButton>
      </Row>
      {/* `sx` because `containerType` has no Chakra style prop. */}
      <Box width="100%" sx={{ containerType: "inline-size" }}>
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns={`repeat(${LATEST_IDEAS_LIMIT}, minmax(0, 1fr))`}
          sx={COLUMN_STEPS}
        >
          {children}
        </Box>
      </Box>
    </Column>
  );
}

export function LatestIdeasSkeletonTiles() {
  return SKELETON_KEYS.map((key) => (
    <Skeleton key={key} isLoading>
      <SkeletonBox width="100%" sx={{ aspectRatio: "1" }} borderRadius="none" />
    </Skeleton>
  ));
}
